import './App.css'

import React, { useEffect, useState } from 'react'
import { Route, BrowserRouter as Router } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { loginToIsbyb, setKeycloak, setKeycloakUser } from './actions/keycloak.js'
import AnalysisContainer from './containers/AnalysisContainer.js'
import { DEFAULT_BEAMLINES } from './constants/Config.js'
import EmMovieContainer from './containers/EmMovieContainer.js'
import EmSessionContainer from './containers/EmSessionContainer.js'
import Footer from './components/Footer.js'
import ISPyB from './config/config.js'
import JobsContainer from './containers/JobsContainer.js'
import Loading from './components/Loading.js'
import MXAutoprocessingContainer from './containers/MXAutoprocessingContainer.js'
import MXExperimentsContainer from './containers/mx/MXExperimentsContainer'
import MXLoadSampleChanger from './containers/mx/MXLoadSampleChanger'
import MXSessionContainer from './containers/MXSessionContainer.js'
import MXWorflowContainer from './containers/MXWorflowContainer.js'
import MenuContainer from './containers/MenuContainer'
import NewShipment from 'components/MXShipment/NewShipment'
import PropTypes from 'prop-types'
import ProposalsContainer from './containers/ProposalsContainer.js'
import ProteinList from 'components/Mx/ProteinList.js'
import SaxsAbInitioContainer from './containers/saxs/SaxsAbInitioContainer'
import SaxsBuffersContainer from './containers/saxs/SaxsBuffersContainer'
import SaxsDataReductionContainer from './containers/saxs/SaxsDataReductionContainer'
import SaxsExperimentsContainer from './containers/saxs/SaxsExperimentsContainer'
import SaxsMacromoleculesContainer from './containers/saxs/SaxsMacromoleculesContainer'
import SelectionContainer from './containers/SelectionContainer.js'
import SessionsContainer from './containers/SessionsContainer.js'
import ShipmentList from 'components/MXShipment/ShipmentList.js'
import ShippingMain from 'components/MXShipment/ShippingMain.js'
import { fetchProposals } from './actions/proposals.js'
import { setUpConfiguration } from './actions/configuration'
import tinyParams from 'tiny-params'
import { withKeycloak } from '@react-keycloak/web'

const App = ({ keycloak }) => {
  const [loadingInProgress, setLoadingInProgress] = useState(false)
  const [loading, setLoading] = useState(true)
  const [errorOnLoading, setErrorOnLoading] = useState(false)
  const user = useSelector(state => state.user)

  const dispatch = useDispatch()

  useEffect(() => dispatch(setUpConfiguration()), [dispatch])

  useEffect(() => {
    const init = async () => {
      if (keycloak?.authenticated && !loadingInProgress && loading) {
        setLoadingInProgress(true)
        try {
          dispatch(setKeycloak(keycloak))
          const server = ISPyB.sites.find(s => s.name === DEFAULT_BEAMLINES).server
          const keycloakUser = await keycloak.loadUserProfile()
          setLoading(false)
          const data = await dispatch(loginToIsbyb(server, DEFAULT_BEAMLINES, keycloakUser.username))
          keycloakUser.server = server
          keycloakUser.token = data.token
          dispatch(setKeycloakUser(keycloakUser))
          dispatch(fetchProposals({ server, token: data.token }))

          //BMO 20210419 : added that when we removed login.js but it seems to crash the app so now it's commented
          // if (data.roles.find(role => role === 'Manager')) {
          //   dispatch(
          //     fetchSessionsByDate(
          //       { server: server, token: data.token },
          //       format(new Date(), 'yyyyMMdd'),
          //       format(new Date(), 'yyyyMMdd')
          //     )
          //   )
          // } else {
          //   dispatch(fetchSessions({ server: server, token: data.token }))
          // }
          // dispatch(loadJobs(keycloakUser.username))
        } catch (e) {
          setErrorOnLoading(true)
        }
      }
    }
    init()
    if (keycloak?.authenticated && user.token && loadingInProgress) {
      setLoadingInProgress(false)
      setLoading(false)
    }
  }, [dispatch, keycloak, loading, loadingInProgress, user.token])

  const [showNewShipment, setShowNewShipment] = useState(false)

  return (
    <>
      {errorOnLoading && <div>Error while initialisation to IspyB.</div>}
      {loading || !user.token ? (
        <Loading message="Connecting to IspyB..." />
      ) : (
        <Router basename={process.env.PUBLIC_URL}>
          <div>
            <MenuContainer showNewShipment={setShowNewShipment} keycloak={keycloak} />

            <Route exact path="/" component={ProposalPage} />
            <Route exact path="/home" component={ProposalPage} />
            <Route exact path="/proposals" component={ProposalPage} />
            <Route exact path="/sessions" component={SessionPage} />

            <Route exact path="/sessions/dates/:startDate/:endDate" render={p => <SessionPage {...p} />} />

            <Route exact path="/sessions/proposal/:proposal" render={p => <SessionPage {...p} />} />

            <Route
              exact
              path="/sessions/mx/proposal/:proposalName/session/:sessionId/datasets"
              component={MXSessionPage}
            />
            <Route
              exact
              path="/sessions/mx/proposal/:proposalName/session/:sessionId/workflow/:workflowId/steps/:workflowStepIds"
              component={MXWorflowPage}
            />
            <Route
              exact
              path="/sessions/mx/proposal/:proposalName/datacollection/:datacollectionid/autoprocintegration/:autoprocintegrationid"
              component={MXAutoprocIntegrationPage}
            />
            <Route
              exact
              path="/sessions/saxs/proposal/:proposalName/session/:sessionId/experiments"
              component={SaxsExperimentsAndDatasetPage}
            />
            <Route
              exact
              path="/sessions/saxs/proposal/:proposalName/session/:sessionId/experiments/:experimentId/dataCollection/:dataCollectionId/reduction"
              component={SaxsDataCollectionReductionPage}
            />
            <Route
              exact
              path="/sessions/saxs/proposal/:proposalName/session/:sessionId/experiments/:experimentId/dataCollection/:dataCollectionId/abinitio"
              component={SaxsDataCollectionAbInitioPage}
            />

            <Route
              exact
              path="/sessions/em/proposal/:proposalName/session/:sessionId/datasets"
              component={EmSessionPage}
            />
            <Route
              exact
              path="/sessions/em/proposal/:proposalName/datacollection/:dataCollectionId/movies"
              component={EmMoviePage}
            />

            <Route exact path="/selected" render={p => <SelectionPage {...p} />} />

            <Route exact path="/analysis/:tool" render={p => <AnalysisPage {...p} />} />

            <Route exact path="/jobs" render={p => <JobsPage {...p} />} />

            <Route exact path="/prepare/experiments" component={MXPrepareExperimentsPage} />
            <Route exact path="/prepare/mxshipments/:shippingId" component={MXPrepareShipmentPage} />
            <Route exact path="/prepare/mxshipments" component={MXShipmentsPage} />
            <Route exact path="/prepare/myproteins" component={MXMyProteinsPage} />
            <Route exact path="/prepare/main/loadSampleChanger" component={MXLoadSampleChangerPage} />
            <Route exact path="/prepare/macromolecule" component={SaxsPrepareExpWithMacromoleculePage} />
            <Route exact path="/prepare/buffer" component={SaxsPrepareExpWithBufferPage} />
            <Route exact path="/prepare/myExperiments" component={SaxsPrepareExpWithMyExperimentsPage} />
            <Footer />
            {showNewShipment ? <NewShipment handleClose={() => setShowNewShipment(false)} /> : null}
          </div>
        </Router>
      )}
    </>
  )
}

function JobsPage() {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px' }}>
      <JobsContainer />
    </div>
  )
}

function MXWorflowPage(props) {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px' }}>
      <MXWorflowContainer
        proposal={props.match.params.proposalName}
        sessionId={props.match.params.sessionId}
        workflowStepIds={props.match.params.workflowStepIds}
        workflowId={props.match.params.workflowId}
      />
    </div>
  )
}

function MXAutoprocIntegrationPage(props) {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px' }}>
      <MXAutoprocessingContainer
        proposal={props.match.params.proposalName}
        autoprocIntegrationId={props.match.params.autoprocintegrationid}
        dataCollectionId={props.match.params.datacollectionid}
      />
    </div>
  )
}

function ProposalPage() {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px', marginBottom: '70px' }}>
      <ProposalsContainer />
    </div>
  )
}

function SessionPage(props) {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px', marginBottom: '70px' }}>
      <SessionsContainer
        history={props.history}
        proposal={props.match.params.proposal}
        startDate={props.match.params.startDate}
        endDate={props.match.params.endDate}
      />
    </div>
  )
}

function AnalysisPage(props) {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px' }}>
      <AnalysisContainer tool={props.match.params.tool} params={tinyParams(window.location.href)} />
    </div>
  )
}

function SaxsExperimentsAndDatasetPage(props) {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px' }}>
      <SaxsExperimentsContainer sessionId={props.match.params.sessionId} proposal={props.match.params.proposalName} />
    </div>
  )
}

function SaxsDataCollectionReductionPage(props) {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px' }}>
      <SaxsDataReductionContainer
        experimentId={props.match.params.experimentId}
        dataCollectionId={props.match.params.dataCollectionId}
        proposal={props.match.params.proposalName}
        sessionId={props.match.params.sessionId}
      />
    </div>
  )
}

function SaxsDataCollectionAbInitioPage(props) {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px' }}>
      <SaxsAbInitioContainer
        dataCollectionId={props.match.params.dataCollectionId}
        proposal={props.match.params.proposalName}
      />
    </div>
  )
}

function MXPrepareExperimentsPage() {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px', marginBottom: '70px' }}>
      <MXExperimentsContainer />
    </div>
  )
}

function MXPrepareShipmentPage() {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px', marginBottom: '70px' }}>
      <ShippingMain />
    </div>
  )
}

function MXShipmentsPage() {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px', marginBottom: '70px' }}>
      <ShipmentList />
    </div>
  )
}

function MXMyProteinsPage() {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px', marginBottom: '70px' }}>
      <ProteinList />
    </div>
  )
}

function MXLoadSampleChangerPage() {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px', marginBottom: '70px' }}>
      <MXLoadSampleChanger />
    </div>
  )
}

function SaxsPrepareExpWithMacromoleculePage() {
  return <SaxsMacromoleculesContainer />
}

function SaxsPrepareExpWithBufferPage() {
  return <SaxsBuffersContainer />
}

function SaxsPrepareExpWithMyExperimentsPage() {
  return 'Not implemented yet'
}

function EmSessionPage(props) {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px' }}>
      <EmSessionContainer sessionId={props.match.params.sessionId} proposal={props.match.params.proposalName} />
    </div>
  )
}

function EmMoviePage(props) {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px' }}>
      <EmMovieContainer
        dataCollectionId={props.match.params.dataCollectionId}
        proposal={props.match.params.proposalName}
      />
    </div>
  )
}

function MXSessionPage(props) {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px' }}>
      <MXSessionContainer sessionId={props.match.params.sessionId} proposal={props.match.params.proposalName} />
    </div>
  )
}

function SelectionPage() {
  return (
    <div style={{ marginTop: '30px', marginLeft: '30px', marginRight: '30px', marginBottom: '70px' }}>
      <SelectionContainer />
    </div>
  )
}

App.propTypes = {
  keycloak: PropTypes.object,
  user: PropTypes.object,
}

export default withKeycloak(App)
